import {
  AdminPanelSettings,
  Badge,
  Business,
  ContactsOutlined,
  Dataset,
  DevicesOther,
  Extension,
  FactCheck,
  FolderOpen,
  HomeOutlined,
  InfoOutlined,
  Inventory2,
  PeopleOutline,
  PrecisionManufacturing,
  RequestQuote,
  Rule,
  Schema,
  Science,
  TextSnippetOutlined,
  TrendingUp,
  Workspaces,
} from "@mui/icons-material"

import { useIsAuthenticated, useMsal } from "@azure/msal-react"

import { Box, CircularProgress, Typography } from "@mui/material"
import { DashboardLayout, PageContainer, type Navigation } from "@toolpad/core"
import { type Session } from "@toolpad/core/AppProvider"
import { ReactRouterAppProvider } from "@toolpad/core/react-router"
import { ResponsiveDialog } from "components/ResponsiveDialog/ResponsiveDialog"
import { useStoreActions } from "hooks/storeHooks"
import { LoginPage } from "pages/LoginPage/LoginPage"
import { useEffect, useMemo, useState } from "react"
import { Outlet } from "react-router-dom"
import { baseTheme } from "themes/BaseTheme"
import { getFormattedVersion } from "utils/version"
const AdminRootLayout = () => {
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const setAuthenticatedUser = useStoreActions((actions) => actions.setAuthenticatedUser)

  const [session, setSession] = useState<Session | null>(null)
  const [loginDialogOpen, setLoginDialogOpen] = useState(false)

  const [showData, setShowData] = useState(false)
  const [isSidebarMini, setIsSidebarMini] = useState(false)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "F8") {
        setShowData((prev) => !prev)
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  const navigation: Navigation = useMemo(() => {
    // Grouped navigation (expanded sidebar)
    const groupedNav: Navigation = [
      {
        title: "Dashboard",
        icon: <HomeOutlined />,
        segment: "dashboard",
      },

      // { kind: "divider" },
      // {
      //   kind: "header",
      //   title: "EUT Management",
      // },
      // {
      //   title: "EUT Management",
      //   icon: <Hardware />,
      //   segment: "eutmanagement",
      // },

      { kind: "divider" },
      {
        title: "Projektportal",
        icon: <Workspaces />,
        segment: "projectportal",
        children: [
          { title: "Verkaufschancen", icon: <TrendingUp />, segment: "leads" },
          { title: "Projekte", icon: <FolderOpen />, segment: "projects" },
        ],
      },

      { kind: "divider" },
      // Removed header to rely on iconized parent item below
      {
        title: "Administration",
        icon: <AdminPanelSettings />,
        segment: "admin",
        children: [
          { title: "EUT-Eigenschaftsgruppen", icon: <DevicesOther />, segment: "devicedata" },
          { title: "Textvorlagen", icon: <TextSnippetOutlined />, segment: "textblocks" },
          { title: "Skillmanagement", icon: <Badge />, segment: "skillmanagement" },
          { title: "Arbeitsgänge", icon: <Schema />, segment: "tasks" },
          { title: "Normen", icon: <Rule />, segment: "norms" },
          { title: "AUX", icon: <Extension />, segment: "auxequipment" },
          { title: "Testgeräte", icon: <PrecisionManufacturing />, segment: "testequipment" },
          { title: "Prüfverfahren", icon: <FactCheck />, segment: "examinations" },
          { title: "Laborbereich", icon: <Science />, segment: "laboratories" },
          {
            title: "Pass/Fail-Kriterienkatalog",
            icon: <FactCheck />,
            segment: "criteriacatalogs",
          },
        ],
      },
    ]

    if (showData) {
      groupedNav.push({ kind: "divider" } as const)
      groupedNav.push({ kind: "header", title: "Data" } as const)
      groupedNav.push({
        title: "Data",
        icon: <Dataset />,
        segment: "data",
        children: [
          { title: "Kontakte", icon: <ContactsOutlined />, segment: "contacts" },
          { title: "Kunden", icon: <Business />, segment: "customers" },
          { title: "Verkaufsquoten", icon: <RequestQuote />, segment: "salesquotes" },
          { title: "Artikel", icon: <Inventory2 />, segment: "stockitems" },
          { title: "Benutzer", icon: <PeopleOutline />, segment: "users" },
        ],
      })
    }

    if (!isSidebarMini) return groupedNav

    // Flattened navigation (mini/collapsed sidebar): promote children to top-level
    const flattenedNav: Navigation = []
    let seenPageItems = 0
    let processedChildrenGroups = 0
    for (const item of groupedNav) {
      if ((item as any).kind === "divider" || (item as any).kind === "header") {
        // skip in mini to save vertical space
        continue
      }
      const pageItem = item as any
      if (pageItem.children) {
        // add each child as a top-level item, prefixing parent segment if present
        const parentSegment: string | undefined = pageItem.segment
        for (const child of pageItem.children as any[]) {
          const childSegment: string | undefined = child.segment
          const prefixedSegment = parentSegment
            ? `${parentSegment}${childSegment ? `/${childSegment}` : ""}`
            : childSegment
          flattenedNav.push({ ...child, segment: prefixedSegment })
        }
        processedChildrenGroups += 1
        if (processedChildrenGroups === 1) {
          flattenedNav.push({ kind: "divider" } as const)
        }
      } else {
        flattenedNav.push(pageItem)
        seenPageItems += 1
        if (seenPageItems === 1) {
          flattenedNav.push({ kind: "divider" } as const)
        }
      }
    }
    return flattenedNav
  }, [showData, isSidebarMini])

  // make logo xmas if it is december
  const isDecember = new Date().getMonth() === 11
  const logo = isDecember ? "/logoxmas.png" : "/logo.png"
  const logoAlt = isDecember ? "Logo Xmas" : "Logo"

  const branding = {
    title: "",
    logo: (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <img src={logo} alt={logoAlt} style={{ maxHeight: "40px" }} />
      </Box>
    ),
    homeUrl: "/dashboard",
  }

  useEffect(() => {
    if (isAuthenticated && accounts[0]) {
      if (!session || session.user?.email !== accounts[0].username) {
        setSession({
          user: {
            name: accounts[0].name || accounts[0].username,
            email: accounts[0].username,
          },
        })
        setAuthenticatedUser(accounts[0])
      }
      setLoginDialogOpen(false)
    } else if (!isAuthenticated && session && inProgress === "none") {
      setSession(null)
      setAuthenticatedUser(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accounts, session, inProgress])

  const authentication = useMemo(() => {
    return {
      signIn: () => {
        if (!isAuthenticated) {
          setLoginDialogOpen(true)
        }
      },
      signOut: () => {
        instance
          .logoutPopup()
          .then(() => {
            setSession(null)
            setAuthenticatedUser(null)
          })
          .catch((e) => {
            console.error("Logout failed:", e)
          })
      },
    }
  }, [instance, isAuthenticated, setAuthenticatedUser])

  const handleLoginSuccess = () => {
    setLoginDialogOpen(false)
  }

  if (inProgress === "startup" || inProgress === "handleRedirect") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Initializing authentication...</Typography>
      </Box>
    )
  }

  // Sidebar footer slot that also reports mini state
  const SidebarFooterWithVersion = ({ mini }: { mini: boolean }) => {
    useEffect(() => {
      setIsSidebarMini(mini)
    }, [mini])
    return (
      <Box
        sx={{
          p: 2,

          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <InfoOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
          {getFormattedVersion()}
        </Typography>
      </Box>
    )
  }

  return (
    <ReactRouterAppProvider
      theme={baseTheme}
      session={session}
      navigation={navigation}
      branding={branding}
      authentication={authentication}
    >
      <DashboardLayout slots={{ sidebarFooter: SidebarFooterWithVersion }}>
        <PageContainer maxWidth={false} breadcrumbs={[{ path: "", title: "" }]}>
          <Outlet />
        </PageContainer>
      </DashboardLayout>

      {loginDialogOpen && !isAuthenticated && (
        <ResponsiveDialog
          open={loginDialogOpen}
          onClose={() => setLoginDialogOpen(false)}
          fullScreen
        >
          <LoginPage onLoginSuccess={handleLoginSuccess} />
        </ResponsiveDialog>
      )}
    </ReactRouterAppProvider>
  )
}

export default AdminRootLayout
